import { CapacitorSQLite, SQLiteConnection, SQLiteDBConnection } from '@capacitor-community/sqlite';

// ============================================================
// NurWallet Database Service (ROBUST WEB + NATIVE)
// ============================================================

// Detect platform
const getPlatform = (): string => {
  try {
    if (typeof window !== 'undefined' && 'Capacitor' in window) {
      return (window as any).Capacitor.getPlatform();
    }
  } catch (e) {}
  return 'web';
};

const isNative = (): boolean => {
  const platform = getPlatform();
  return platform === 'android' || platform === 'ios';
};

// ============================================================
// MOCK DB SERVICE (for web / when SQLite fails)
// ============================================================

class MockDbService {
  private storage: Record<string, any[]> = {};
  private initialized = false;

  async init(): Promise<void> {
    if (this.initialized) return;

    try {
      const saved = localStorage.getItem('nurwallet_mock_db');
      if (saved) {
        this.storage = JSON.parse(saved);
      }
    } catch (e) {
      console.log('No existing mock data');
    }

    // Initialize tables
    this.storage['users'] = this.storage['users'] || [];
    this.storage['takaful_plans'] = this.storage['takaful_plans'] || [];
    this.storage['retirement_plans'] = this.storage['retirement_plans'] || [];
    this.storage['financial_goals'] = this.storage['financial_goals'] || [];
    this.storage['zakat_records'] = this.storage['zakat_records'] || [];
    this.storage['hajj_plans'] = this.storage['hajj_plans'] || [];
    this.storage['transactions'] = this.storage['transactions'] || [];
    this.storage['assets'] = this.storage['assets'] || [];
    this.storage['liabilities'] = this.storage['liabilities'] || [];
    this.storage['chat_sessions'] = this.storage['chat_sessions'] || [];
    this.storage['chat_messages'] = this.storage['chat_messages'] || [];
    this.storage['advisors'] = this.storage['advisors'] || [];
    this.storage['advisor_bookings'] = this.storage['advisor_bookings'] || [];
    this.storage['notifications'] = this.storage['notifications'] || [];
    this.storage['ai_usage'] = this.storage['ai_usage'] || [];
    this.storage['reminders'] = this.storage['reminders'] || [];

    // Migrate existing retirement plans to add new fields
    this.storage['retirement_plans'].forEach((plan: any) => {
      if (plan.monthly_expenses === undefined) plan.monthly_expenses = 0;
      if (plan.expected_return_rate === undefined) plan.expected_return_rate = 6;
      if (plan.inflation_rate === undefined) plan.inflation_rate = 3.5;
      if (plan.total_projected === undefined) plan.total_projected = 0;
      if (plan.shortfall === undefined) plan.shortfall = 0;
      if (plan.inflated_monthly_expenses === undefined) plan.inflated_monthly_expenses = 0;
      if (plan.progress_percent === undefined) plan.progress_percent = 0;
    });

    // Create default guest user
    if (!this.storage['users'].find((u: any) => u.email === 'guest@nurwallet.local')) {
      this.storage['users'].push({
        id: 'guest-001',
        email: 'guest@nurwallet.local',
        password: 'guest123',
        full_name: 'Guest User',
        family_status: 'SINGLE',
        tier: 'free',
        health_score: 0,
        health_score_zone: 'CRITICAL',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      });
    }

    this.initialized = true;
    this.persist();
    console.log('✅ Mock DB initialized');
  }

  private persist() {
    try {
      localStorage.setItem('nurwallet_mock_db', JSON.stringify(this.storage));
    } catch (e) {
      console.error('Failed to persist mock DB:', e);
    }
  }

  private generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  // USER METHODS
  async createUser(userData: any): Promise<any> {
    await this.init();
    const id = this.generateId();
    const now = new Date().toISOString();
    const user = {
      id,
      email: userData.email,
      password: userData.password,
      full_name: userData.fullName || userData.full_name,
      family_status: userData.familyStatus || userData.family_status || 'SINGLE',
      tier: 'free',
      health_score: 0,
      health_score_zone: 'CRITICAL',
      created_at: now,
      updated_at: now,
    };
    this.storage['users'].push(user);
    this.persist();
    return this.mapUser(user);
  }

  async getUserById(id: string): Promise<any> {
    await this.init();
    const user = this.storage['users'].find((u: any) => u.id === id);
    if (!user) throw new Error('User not found');
    return this.mapUser(user);
  }

  async getUserByEmail(email: string): Promise<any | null> {
    await this.init();
    const user = this.storage['users'].find((u: any) => u.email === email);
    return user ? this.mapUser(user) : null;
  }

  async getAllUsers(): Promise<any[]> {
    await this.init();
    return this.storage['users'].map((u: any) => this.mapUser(u));
  }

  async updateUser(id: string, updates: any): Promise<void> {
    await this.init();
    const idx = this.storage['users'].findIndex((u: any) => u.id === id);
    if (idx >= 0) {
      Object.assign(this.storage['users'][idx], updates);
      this.storage['users'][idx].updated_at = new Date().toISOString();
      this.persist();
    }
  }

  // TAKAFUL METHODS
  async saveTakaful(data: any): Promise<any> {
    await this.init();
    const now = new Date().toISOString();
    const existingIdx = this.storage['takaful_plans'].findIndex((p: any) => p.user_id === data.userId);

    if (existingIdx >= 0) {
      Object.assign(this.storage['takaful_plans'][existingIdx], {
        annual_income: data.annualIncome,
        outstanding_debts: data.outstandingDebts,
        education_fund: data.educationFund,
        funeral_expenses: data.funeralExpenses,
        emergency_fund_months: data.emergencyFundMonths,
        dependents: data.dependents,
        existing_coverage: data.existingCoverage,
        recommended_coverage: data.recommendedCoverage,
        premium_estimate: data.premiumEstimate,
        updated_at: now,
      });
      this.persist();
      return { ...data, updatedAt: now };
    } else {
      const id = this.generateId();
      const plan = {
        id,
        user_id: data.userId,
        annual_income: data.annualIncome,
        outstanding_debts: data.outstandingDebts,
        education_fund: data.educationFund,
        funeral_expenses: data.funeralExpenses,
        emergency_fund_months: data.emergencyFundMonths,
        dependents: data.dependents,
        existing_coverage: data.existingCoverage,
        recommended_coverage: data.recommendedCoverage,
        premium_estimate: data.premiumEstimate,
        created_at: now,
        updated_at: now,
      };
      this.storage['takaful_plans'].push(plan);
      this.persist();
      return { ...data, id, createdAt: now, updatedAt: now };
    }
  }

  async getTakafulPlan(userId: string): Promise<any | null> {
    await this.init();
    const plan = this.storage['takaful_plans'].find((p: any) => p.user_id === userId);
    if (!plan) return null;
    return {
      id: plan.id,
      userId: plan.user_id,
      annualIncome: plan.annual_income,
      outstandingDebts: plan.outstanding_debts,
      educationFund: plan.education_fund,
      funeralExpenses: plan.funeral_expenses,
      emergencyFundMonths: plan.emergency_fund_months,
      dependents: plan.dependents,
      existingCoverage: plan.existing_coverage,
      recommendedCoverage: plan.recommended_coverage,
      premiumEstimate: plan.premium_estimate,
      createdAt: plan.created_at,
      updatedAt: plan.updated_at,
    };
  }

  // RETIREMENT METHODS
  async saveRetirement(data: any): Promise<any> {
    await this.init();
    const now = new Date().toISOString();
    const existingIdx = this.storage['retirement_plans'].findIndex((p: any) => p.user_id === data.userId);

    const planData = {
      current_age: data.currentAge,
      retirement_age: data.retirementAge,
      life_expectancy: data.lifeExpectancy,
      current_savings: data.currentSavings,
      monthly_contribution: data.monthlyContribution,
      target_amount: data.targetAmount,
      monthly_expenses: data.monthlyExpenses || 0,
      expected_return_rate: data.expectedReturnRate || 6,
      inflation_rate: data.inflationRate || 3.5,
      total_projected: data.totalProjected || 0,
      shortfall: data.shortfall || 0,
      inflated_monthly_expenses: data.inflatedMonthlyExpenses || 0,
      progress_percent: data.progressPercent || 0,
      updated_at: now,
    };

    if (existingIdx >= 0) {
      Object.assign(this.storage['retirement_plans'][existingIdx], planData);
      this.persist();
      return { ...data, updatedAt: now };
    } else {
      const id = this.generateId();
      const plan = { id, user_id: data.userId, ...planData, created_at: now };
      this.storage['retirement_plans'].push(plan);
      this.persist();
      return { ...data, id, createdAt: now, updatedAt: now };
    }
  }

  async getRetirementPlan(userId: string): Promise<any | null> {
    await this.init();
    const plan = this.storage['retirement_plans'].find((p: any) => p.user_id === userId);
    if (!plan) return null;
    return {
      id: plan.id,
      userId: plan.user_id,
      currentAge: plan.current_age,
      retirementAge: plan.retirement_age,
      lifeExpectancy: plan.life_expectancy,
      currentSavings: plan.current_savings,
      monthlyContribution: plan.monthly_contribution,
      targetAmount: plan.target_amount,
      monthlyExpenses: plan.monthly_expenses,
      expectedReturnRate: plan.expected_return_rate,
      inflationRate: plan.inflation_rate,
      totalProjected: plan.total_projected,
      shortfall: plan.shortfall,
      inflatedMonthlyExpenses: plan.inflated_monthly_expenses,
      progressPercent: plan.progress_percent,
      createdAt: plan.created_at,
      updatedAt: plan.updated_at,
    };
  }

  // GOALS METHODS
  async createGoal(userId: string, goal: any): Promise<any> {
    await this.init();
    const now = new Date().toISOString();
    const id = this.generateId();
    const newGoal = {
      id,
      user_id: userId,
      title: goal.title,
      description: goal.description,
      target_amount: goal.targetAmount || 0,
      current_amount: goal.currentAmount || 0,
      category: goal.category || 'general',
      priority: goal.priority || 'medium',
      status: goal.status || 'active',
      created_at: now,
      updated_at: now,
    };
    this.storage['financial_goals'].push(newGoal);
    this.persist();
    return { ...goal, id, userId, createdAt: now, updatedAt: now };
  }

  // ZAKAT METHODS
  async saveZakat(data: any): Promise<any> {
    await this.init();
    const now = new Date().toISOString();
    const existingIdx = this.storage['zakat_records'].findIndex((z: any) => z.user_id === data.userId && z.year === data.year);

    if (existingIdx >= 0) {
      Object.assign(this.storage['zakat_records'][existingIdx], {
        cash: data.cash,
        investments: data.investments,
        gold_value: data.goldValue,
        silver_value: data.silverValue,
        business_assets: data.businessAssets,
        receivables: data.receivables,
        deductible_liabilities: data.deductibleLiabilities,
        total_wealth: data.totalWealth,
        nisab_threshold: data.nisabThreshold,
        zakat_payable: data.zakatPayable,
        updated_at: now,
      });
      this.persist();
      return { ...data, updatedAt: now };
    } else {
      const id = this.generateId();
      const record = {
        id,
        user_id: data.userId,
        year: data.year,
        cash: data.cash,
        investments: data.investments,
        gold_value: data.goldValue,
        silver_value: data.silverValue,
        business_assets: data.businessAssets,
        receivables: data.receivables,
        deductible_liabilities: data.deductibleLiabilities,
        total_wealth: data.totalWealth,
        nisab_threshold: data.nisabThreshold,
        zakat_payable: data.zakatPayable,
        created_at: now,
        updated_at: now,
      };
      this.storage['zakat_records'].push(record);
      this.persist();
      return { ...data, id, createdAt: now, updatedAt: now };
    }
  }

  async getZakatRecords(userId: string): Promise<any[]> {
    await this.init();
    return this.storage['zakat_records']
      .filter((z: any) => z.user_id === userId)
      .sort((a: any, b: any) => b.year - a.year)
      .map((z: any) => ({
        id: z.id,
        userId: z.user_id,
        year: z.year,
        cash: z.cash,
        investments: z.investments,
        goldValue: z.gold_value,
        silverValue: z.silver_value,
        businessAssets: z.business_assets,
        receivables: z.receivables,
        deductibleLiabilities: z.deductible_liabilities,
        totalWealth: z.total_wealth,
        nisabThreshold: z.nisab_threshold,
        zakatPayable: z.zakat_payable,
        createdAt: z.created_at,
        updatedAt: z.updated_at,
      }));
  }

  // HAJJ METHODS
  async saveHajj(data: any): Promise<any> {
    await this.init();
    const now = new Date().toISOString();
    const existingIdx = this.storage['hajj_plans'].findIndex((p: any) => p.user_id === data.userId && p.target_year === data.targetYear);

    if (existingIdx >= 0) {
      Object.assign(this.storage['hajj_plans'][existingIdx], {
        estimated_cost: data.estimatedCost,
        monthly_saving: data.monthlySaving,
        total_saved: data.totalSaved,
        progress_percent: data.progressPercent,
        package_type: data.packageType,
        travelers: data.travelers,
        updated_at: now,
      });
      this.persist();
      return { ...data, updatedAt: now };
    } else {
      const id = this.generateId();
      const plan = {
        id,
        user_id: data.userId,
        target_year: data.targetYear,
        estimated_cost: data.estimatedCost,
        monthly_saving: data.monthlySaving,
        total_saved: data.totalSaved,
        progress_percent: data.progressPercent,
        package_type: data.packageType,
        travelers: data.travelers,
        created_at: now,
        updated_at: now,
      };
      this.storage['hajj_plans'].push(plan);
      this.persist();
      return { ...data, id, createdAt: now, updatedAt: now };
    }
  }

  async getHajjPlans(userId: string): Promise<any[]> {
    await this.init();
    return this.storage['hajj_plans']
      .filter((p: any) => p.user_id === userId)
      .sort((a: any, b: any) => b.target_year - a.target_year)
      .map((p: any) => ({
        id: p.id,
        userId: p.user_id,
        targetYear: p.target_year,
        estimatedCost: p.estimated_cost,
        monthlySaving: p.monthly_saving,
        totalSaved: p.total_saved,
        progressPercent: p.progress_percent,
        packageType: p.package_type,
        travelers: p.travelers,
        createdAt: p.created_at,
        updatedAt: p.updated_at,
      }));
  }

  // TRANSACTION METHODS
  async createTransaction(data: any): Promise<any> {
    await this.init();
    const id = this.generateId();
    const now = new Date().toISOString();
    const tx = {
      id, user_id: data.userId, type: data.type, category: data.category,
      amount: data.amount, description: data.description || null,
      date: data.date || now, is_recurring: data.isRecurring ? 1 : 0,
      created_at: now,
    };
    this.storage['transactions'] = this.storage['transactions'] || [];
    this.storage['transactions'].push(tx);
    this.persist();
    return { id, ...data, createdAt: now };
  }

  async getTransactions(userId: string, filters?: any): Promise<any[]> {
    await this.init();
    this.storage['transactions'] = this.storage['transactions'] || [];
    let txs = this.storage['transactions'].filter((t: any) => t.user_id === userId);
    if (filters?.type) {
      txs = txs.filter((t: any) => t.type === filters.type);
    }
    return txs.sort((a: any, b: any) => new Date(b.date).getTime() - new Date(a.date).getTime())
      .map((t: any) => ({
        id: t.id, type: t.type, category: t.category, amount: t.amount,
        description: t.description, date: t.date, isRecurring: t.is_recurring === 1,
        createdAt: t.created_at,
      }));
  }

  async getTransactionSummary(userId: string): Promise<any> {
    await this.init();
    this.storage['transactions'] = this.storage['transactions'] || [];
    const txs = this.storage['transactions'].filter((t: any) => t.user_id === userId);
    const income = txs.filter((t: any) => t.type === 'INCOME').reduce((sum: number, t: any) => sum + (t.amount || 0), 0);
    const expense = txs.filter((t: any) => t.type === 'EXPENSE').reduce((sum: number, t: any) => sum + (t.amount || 0), 0);
    return { totalIncome: income, totalExpense: expense, net: income - expense };
  }

  async deleteTransaction(id: string, userId: string): Promise<void> {
    await this.init();
    this.storage['transactions'] = this.storage['transactions'] || [];
    this.storage['transactions'] = this.storage['transactions'].filter((t: any) => t.id !== id);
    this.persist();
  }

  // ASSET METHODS
  async createAsset(data: any): Promise<any> {
    await this.init();
    const id = this.generateId();
    const now = new Date().toISOString();
    const asset = {
      id, user_id: data.userId, name: data.name, type: data.type,
      value: data.value, current_value: data.value, is_active: 1, created_at: now,
    };
    this.storage['assets'] = this.storage['assets'] || [];
    this.storage['assets'].push(asset);
    this.persist();
    return { id, ...data, currentValue: data.value, isActive: true, createdAt: now };
  }

  async getAssets(userId: string): Promise<any[]> {
    await this.init();
    this.storage['assets'] = this.storage['assets'] || [];
    return this.storage['assets']
      .filter((a: any) => a.user_id === userId && a.is_active === 1)
      .sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
      .map((a: any) => ({
        id: a.id, name: a.name, type: a.type, value: a.value,
        currentValue: a.current_value, isActive: a.is_active === 1, createdAt: a.created_at,
      }));
  }

  async getAssetSummary(userId: string): Promise<any> {
    await this.init();
    this.storage['assets'] = this.storage['assets'] || [];
    const assets = this.storage['assets'].filter((a: any) => a.user_id === userId && a.is_active === 1);
    const byType: Record<string, number> = {};
    let total = 0;
    for (const a of assets) {
      byType[a.type] = (byType[a.type] || 0) + (a.current_value || 0);
      total += a.current_value || 0;
    }
    return { totalValue: total, byType };
  }

  async deleteAsset(id: string, userId: string): Promise<void> {
    await this.init();
    this.storage['assets'] = this.storage['assets'] || [];
    const idx = this.storage['assets'].findIndex((a: any) => a.id === id);
    if (idx >= 0) {
      this.storage['assets'][idx].is_active = 0;
      this.persist();
    }
  }

  // LIABILITY METHODS
  async createLiability(data: any): Promise<any> {
    await this.init();
    const id = this.generateId();
    const now = new Date().toISOString();
    const liability = {
      id, user_id: data.userId, name: data.name, type: data.type,
      original_amount: data.originalAmount, remaining_amount: data.remainingAmount,
      monthly_payment: data.monthlyPayment, interest_rate: data.interestRate || 0,
      is_active: 1, created_at: now,
    };
    this.storage['liabilities'] = this.storage['liabilities'] || [];
    this.storage['liabilities'].push(liability);
    this.persist();
    return { id, ...data, isActive: true, createdAt: now };
  }

  async getLiabilities(userId: string): Promise<any[]> {
    await this.init();
    this.storage['liabilities'] = this.storage['liabilities'] || [];
    return this.storage['liabilities']
      .filter((l: any) => l.user_id === userId && l.is_active === 1)
      .map((l: any) => ({
        id: l.id, name: l.name, type: l.type, originalAmount: l.original_amount,
        remainingAmount: l.remaining_amount, monthlyPayment: l.monthly_payment,
        interestRate: l.interest_rate, isActive: l.is_active === 1, createdAt: l.created_at,
      }));
  }

  async getLiabilitySummary(userId: string): Promise<any> {
    await this.init();
    this.storage['liabilities'] = this.storage['liabilities'] || [];
    const liabilities = this.storage['liabilities'].filter((l: any) => l.user_id === userId && l.is_active === 1);
    const byType: Record<string, number> = {};
    let totalRemaining = 0;
    let monthlyPayments = 0;
    for (const l of liabilities) {
      byType[l.type] = (byType[l.type] || 0) + (l.remaining_amount || 0);
      totalRemaining += l.remaining_amount || 0;
      monthlyPayments += l.monthly_payment || 0;
    }
    return { totalRemaining, monthlyPayments, byType };
  }

  async deleteLiability(id: string, userId: string): Promise<void> {
    await this.init();
    this.storage['liabilities'] = this.storage['liabilities'] || [];
    const idx = this.storage['liabilities'].findIndex((l: any) => l.id === id);
    if (idx >= 0) {
      this.storage['liabilities'][idx].is_active = 0;
      this.persist();
    }
  }

  // CHAT METHODS
  async createChatSession(userId: string, title: string): Promise<any> {
    await this.init();
    const id = this.generateId();
    const now = new Date().toISOString();
    const session = { id, user_id: userId, title, created_at: now, updated_at: now };
    this.storage['chat_sessions'] = this.storage['chat_sessions'] || [];
    this.storage['chat_sessions'].push(session);
    this.persist();
    return { id, userId, title, createdAt: now, updatedAt: now };
  }

  async getChatSessions(userId: string): Promise<any[]> {
    await this.init();
    this.storage['chat_sessions'] = this.storage['chat_sessions'] || [];
    return this.storage['chat_sessions']
      .filter((s: any) => s.user_id === userId)
      .sort((a: any, b: any) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
      .map((s: any) => ({ ...s, userId: s.user_id }));
  }

  async createChatMessage(data: any): Promise<any> {
    await this.init();
    const id = this.generateId();
    const now = new Date().toISOString();
    const msg = {
      id, user_id: data.userId, session_id: data.sessionId || null,
      role: data.role, content: data.content, created_at: now,
    };
    this.storage['chat_messages'] = this.storage['chat_messages'] || [];
    this.storage['chat_messages'].push(msg);
    if (data.sessionId) {
      const sessionIdx = (this.storage['chat_sessions'] || []).findIndex((s: any) => s.id === data.sessionId);
      if (sessionIdx >= 0) {
        this.storage['chat_sessions'][sessionIdx].updated_at = now;
      }
    }
    this.persist();
    return { id, ...data, createdAt: now };
  }

  async getChatMessages(userId: string, sessionId?: string): Promise<any[]> {
    await this.init();
    this.storage['chat_messages'] = this.storage['chat_messages'] || [];
    let msgs = this.storage['chat_messages'].filter((m: any) => m.user_id === userId);
    if (sessionId) {
      msgs = msgs.filter((m: any) => m.session_id === sessionId);
    }
    return msgs.sort((a: any, b: any) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
  }

  // ADVISOR METHODS
  async getAdvisors(): Promise<any[]> {
    await this.init();
    this.storage['advisors'] = this.storage['advisors'] || [
      { id: 'adv-001', email: 'ahmad@nurwallet.local', full_name: 'Ahmad bin Abdullah', credentials: 'Certified Shariah Financial Advisor', specializations: JSON.stringify(['Zakat', 'Retirement', 'Takaful']), bio: '15 years in Islamic finance.', rating: 4.9, phone: '+60123456789', consultation_fee: 150, is_active: 1 },
      { id: 'adv-002', email: 'siti@nurwallet.local', full_name: 'Siti Nurhaliza', credentials: 'Islamic Estate Planning Specialist', specializations: JSON.stringify(['Estate Planning', 'Hibah', 'Wasiat']), bio: 'Expert in Islamic inheritance law.', rating: 4.8, phone: '+60198765432', consultation_fee: 200, is_active: 1 },
      { id: 'adv-003', email: 'khalid@nurwallet.local', full_name: 'Khalid Ibrahim', credentials: 'Takaful & Risk Management Expert', specializations: JSON.stringify(['Takaful', 'Risk Management', 'Medical']), bio: 'Former Takaful actuary.', rating: 4.7, phone: '+60187654321', consultation_fee: 120, is_active: 1 },
    ];
    this.persist();
    return this.storage['advisors'].filter((a: any) => a.is_active === 1).sort((a: any, b: any) => (b.rating || 0) - (a.rating || 0));
  }

  async createAdvisorBooking(data: any): Promise<any> {
    await this.init();
    const id = this.generateId();
    const now = new Date().toISOString();
    const booking = {
      id, user_id: data.userId, advisor_id: data.advisorId,
      scheduled_at: data.scheduledAt, status: 'PENDING',
      notes: data.notes || null, created_at: now,
    };
    this.storage['advisor_bookings'] = this.storage['advisor_bookings'] || [];
    this.storage['advisor_bookings'].push(booking);
    this.persist();
    return { id, ...data, status: 'PENDING', createdAt: now };
  }

  async getAdvisorBookings(userId: string): Promise<any[]> {
    await this.init();
    this.storage['advisor_bookings'] = this.storage['advisor_bookings'] || [];
    return this.storage['advisor_bookings']
      .filter((b: any) => b.user_id === userId)
      .sort((a: any, b: any) => new Date(b.scheduled_at).getTime() - new Date(a.scheduled_at).getTime());
  }

  // NOTIFICATION METHODS
  async createNotification(data: any): Promise<any> {
    await this.init();
    const id = this.generateId();
    const now = new Date().toISOString();
    const notif = {
      id, user_id: data.userId, type: data.type,
      title: data.title, message: data.message, is_read: 0, created_at: now,
    };
    this.storage['notifications'] = this.storage['notifications'] || [];
    this.storage['notifications'].push(notif);
    this.persist();
    return { id, ...data, isRead: false, createdAt: now };
  }

  async getNotifications(userId: string): Promise<any[]> {
    await this.init();
    this.storage['notifications'] = this.storage['notifications'] || [];
    return this.storage['notifications']
      .filter((n: any) => n.user_id === userId)
      .sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  }

  async markNotificationRead(id: string): Promise<void> {
    await this.init();
    this.storage['notifications'] = this.storage['notifications'] || [];
    const idx = this.storage['notifications'].findIndex((n: any) => n.id === id);
    if (idx >= 0) {
      this.storage['notifications'][idx].is_read = 1;
      this.persist();
    }
  }

  async getUnreadCount(userId: string): Promise<number> {
    await this.init();
    this.storage['notifications'] = this.storage['notifications'] || [];
    return this.storage['notifications'].filter((n: any) => n.user_id === userId && n.is_read === 0).length;
  }

  // AI USAGE METHODS
  async getTodayAIUsage(userId: string): Promise<number> {
    await this.init();
    const today = new Date().toISOString().split('T')[0];
    this.storage['ai_usage'] = this.storage['ai_usage'] || [];
    const usage = this.storage['ai_usage'].find((u: any) => u.user_id === userId && u.date === today);
    return usage?.message_count || 0;
  }

  async incrementAIUsage(userId: string): Promise<void> {
    await this.init();
    const today = new Date().toISOString().split('T')[0];
    this.storage['ai_usage'] = this.storage['ai_usage'] || [];
    const idx = this.storage['ai_usage'].findIndex((u: any) => u.user_id === userId && u.date === today);
    if (idx >= 0) {
      this.storage['ai_usage'][idx].message_count += 1;
    } else {
      this.storage['ai_usage'].push({
        id: this.generateId(), user_id: userId, date: today, message_count: 1,
      });
    }
    this.persist();
  }

  // STRUCTURED DATA METHODS (for AI)
  async getAssetsStructured(userId: string): Promise<any> {
    const summary = await this.getAssetSummary(userId);
    return {
      userId, cash: summary.byType['cash'] || 0, bankAccounts: summary.byType['bank'] || 0,
      goldValue: summary.byType['gold'] || 0, investments: summary.byType['investment'] || 0,
      stocks: summary.byType['stocks'] || 0, unitTrust: summary.byType['unit_trust'] || 0,
      sukuk: summary.byType['sukuk'] || 0, reits: summary.byType['reits'] || 0,
      tabungHaji: summary.byType['tabung_haji'] || 0, epf: summary.byType['epf'] || 0,
      prs: summary.byType['prs'] || 0, propertyValue: summary.byType['property'] || 0,
      businessInventory: summary.byType['business'] || 0, receivables: summary.byType['receivables'] || 0,
      otherAssets: summary.byType['other'] || 0, total: summary.totalValue,
      updatedAt: new Date().toISOString(),
    };
  }

  async getLiabilitiesStructured(userId: string): Promise<any> {
    const summary = await this.getLiabilitySummary(userId);
    return {
      userId, homeLoan: summary.byType['home'] || 0, carLoan: summary.byType['car'] || 0,
      personalLoan: summary.byType['personal'] || 0, creditCard: summary.byType['credit_card'] || 0,
      ptptn: summary.byType['ptptn'] || 0, otherDebts: summary.byType['other'] || 0,
      total: summary.totalRemaining, monthlyPayments: summary.monthlyPayments,
      interestRate: 0, updatedAt: new Date().toISOString(),
    };
  }

  async getZakatSummary(userId: string): Promise<any> {
    const records = await this.getZakatRecords(userId);
    const currentYear = new Date().getFullYear();
    const totalPending = records.filter((z: any) => z.zakatPayable > 0).reduce((sum: number, z: any) => sum + (z.zakatPayable || 0), 0);
    const currentRecord = records.find((z: any) => z.year === currentYear);
    return {
      totalPaid: 0, totalPending, lastPaidYear: records.length > 0 ? records[0].year : null,
      lastPaidAmount: 0, currentLiability: currentRecord?.zakatPayable || 0,
      isLiable: (currentRecord?.zakatPayable || 0) > 0,
    };
  }

  async getIncomeExpenseSummary(userId: string): Promise<any> {
    const txSummary = await this.getTransactionSummary(userId);
    const transactions = await this.getTransactions(userId);
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    const recentTx = transactions.filter((t: any) => new Date(t.date) >= thirtyDaysAgo);
    const incomeBreakdown: Record<string, number> = {};
    const expenseBreakdown: Record<string, number> = {};
    for (const t of recentTx) {
      if (t.type === 'INCOME') incomeBreakdown[t.category] = (incomeBreakdown[t.category] || 0) + t.amount;
      else expenseBreakdown[t.category] = (expenseBreakdown[t.category] || 0) + t.amount;
    }
    return {
      monthlyIncome: txSummary.totalIncome, monthlyExpenses: txSummary.totalExpense,
      monthlySavings: txSummary.net, incomeBreakdown, expenseBreakdown,
    };
  }

  async getNisabThreshold(): Promise<any> {
    const currentYear = new Date().getFullYear();
    return {
      year: currentYear, goldPricePerGram: 330, nisabGrams: 85,
      amount: 28050, effectiveDate: new Date().toISOString(), source: 'Default',
    };
  }

  async updateRetirementPlan(userId: string, updates: any): Promise<void> {
    await this.init();
    const idx = this.storage['retirement_plans'].findIndex((p: any) => p.user_id === userId);
    if (idx >= 0) {
      const fields: Record<string, string> = {
        currentAge: 'current_age', retirementAge: 'retirement_age',
        lifeExpectancy: 'life_expectancy', currentSavings: 'current_savings',
        monthlyContribution: 'monthly_contribution', targetAmount: 'target_amount',
      };
      Object.entries(updates).forEach(([key, value]) => {
        if (fields[key] && value !== undefined) {
          this.storage['retirement_plans'][idx][fields[key]] = value;
        }
      });
      this.storage['retirement_plans'][idx].updated_at = new Date().toISOString();
      this.persist();
    }
  }

  async createReminder(userId: string, reminder: any): Promise<any> {
    await this.init();
    const now = new Date().toISOString();
    const id = this.generateId();
    const newReminder = {
      id, user_id: userId, type: reminder.type || 'general',
      title: reminder.title, message: reminder.message || null,
      scheduled_date: reminder.scheduledDate, is_completed: reminder.isCompleted ? 1 : 0,
      completed_date: reminder.completedDate || null, created_at: now,
    };
    this.storage['reminders'] = this.storage['reminders'] || [];
    this.storage['reminders'].push(newReminder);
    this.persist();
    return { ...reminder, id, userId, createdAt: now };
  }

  // COMPATIBILITY METHODS
  async createZakatRecord(data: any): Promise<any> {
    return this.saveZakat({
      userId: data.userId, year: data.year, cash: Number(data.cashOnHand) || 0,
      investments: Number(data.investments) || 0, goldValue: Number(data.goldValue) || 0,
      silverValue: Number(data.silverValue) || 0, businessAssets: Number(data.businessAssets) || 0,
      receivables: Number(data.receivables) || 0, deductibleLiabilities: Number(data.liabilities) || 0,
      totalWealth: data.totalWealth, nisabThreshold: data.nisabThreshold, zakatPayable: data.zakatPayable,
    });
  }

  async getZakatByYear(userId: string, year: number): Promise<any | null> {
    const records = await this.getZakatRecords(userId);
    return records.find((z: any) => z.year === year) || null;
  }

  async getHajjPlanByYear(userId: string, targetYear: number): Promise<any | null> {
    const plans = await this.getHajjPlans(userId);
    return plans.find((p: any) => p.targetYear === targetYear) || null;
  }

  async updateUserTier(id: string, tier: string): Promise<void> {
    await this.updateUser(id, { tier });
  }

  // DASHBOARD
  async getDashboardData(userId: string): Promise<any> {
    await this.init();
    const user = this.storage['users'].find((u: any) => u.id === userId);
    const takaful = await this.getTakafulPlan(userId);
    const retirement = await this.getRetirementPlan(userId);
    const zakat = await this.getZakatRecords(userId);

    return {
      user: user ? this.mapUser(user) : null,
      financials: {
        netWorth: 0,
        totalAssets: 0,
        totalLiabilities: 0,
        monthlyIncome: 0,
        monthlyExpenses: 0,
        monthlySavings: 0,
      },
      healthScore: { score: user?.health_score || 0, zone: user?.health_score_zone || 'CRITICAL' },
      takaful,
      retirement,
      zakat,
    };
  }

  private mapUser(row: any): any {
    return {
      id: row.id,
      email: row.email,
      password: row.password,
      fullName: row.full_name,
      familyStatus: row.family_status,
      tier: row.tier,
      healthScore: row.health_score,
      healthScoreZone: row.health_score_zone,
      createdAt: row.created_at,
      updatedAt: row.updated_at,
    };
  }
}

// ============================================================
// REAL SQLITE SERVICE (for native Android/iOS)
// ============================================================

class CapacitorDbService {
  private db: SQLiteDBConnection | null = null;
  private initialized = false;
  private initPromise: Promise<void> | null = null;

  async init(): Promise<void> {
    if (this.initialized) return;
    // If an init is already in flight, wait for it rather than starting a second one.
    // This prevents duplicate SQLite connection errors when HomeScreen mounts and
    // fires multiple concurrent callers (useWealthData + loadAllData) simultaneously.
    if (this.initPromise) return this.initPromise;

    this.initPromise = (async () => {
      try {
        const sqlite = new SQLiteConnection(CapacitorSQLite);
        const ret = await sqlite.checkConnectionsConsistency();
        const isConn = (await sqlite.isConnection('NurWalletDB', false)).result;

        if (ret.result && isConn) {
          this.db = await sqlite.retrieveConnection('NurWalletDB', false);
        } else {
          this.db = await sqlite.createConnection('NurWalletDB', false, 'no-encryption', 1, false);
        }

        await this.db.open();
        await this.createTables();
        await this.seedAdvisors();
        this.initialized = true;
        console.log('✅ SQLite initialized successfully');
      } catch (error: any) {
        // Clear the promise so callers can retry after a transient failure
        this.initPromise = null;
        console.error('❌ SQLite init failed:', error);
        throw error;
      }
    })();

    return this.initPromise;
  }

  private async createTables(): Promise<void> {
    const schema = [
      `CREATE TABLE IF NOT EXISTS users (
        id TEXT PRIMARY KEY, email TEXT UNIQUE, password TEXT NOT NULL,
        full_name TEXT, family_status TEXT DEFAULT 'SINGLE',
        tier TEXT DEFAULT 'free', health_score INTEGER DEFAULT 0,
        health_score_zone TEXT DEFAULT 'CRITICAL',
        session_token TEXT, last_login TEXT,
        created_at TEXT DEFAULT CURRENT_TIMESTAMP,
        updated_at TEXT DEFAULT CURRENT_TIMESTAMP
      )`,
      `CREATE TABLE IF NOT EXISTS takaful_plans (
        id TEXT PRIMARY KEY, user_id TEXT NOT NULL UNIQUE,
        annual_income REAL DEFAULT 0, outstanding_debts REAL DEFAULT 0,
        education_fund REAL DEFAULT 0, funeral_expenses REAL DEFAULT 0,
        emergency_fund_months INTEGER DEFAULT 12, dependents INTEGER DEFAULT 0,
        existing_coverage REAL DEFAULT 0, recommended_coverage REAL DEFAULT 0,
        premium_estimate REAL DEFAULT 0,
        created_at TEXT DEFAULT CURRENT_TIMESTAMP,
        updated_at TEXT DEFAULT CURRENT_TIMESTAMP
      )`,
      `CREATE TABLE IF NOT EXISTS retirement_plans (
        id TEXT PRIMARY KEY, user_id TEXT NOT NULL UNIQUE,
        current_age INTEGER DEFAULT 30, retirement_age INTEGER DEFAULT 60,
        life_expectancy INTEGER DEFAULT 80, current_savings REAL DEFAULT 0,
        monthly_contribution REAL DEFAULT 0, target_amount REAL DEFAULT 0,
        monthly_expenses REAL DEFAULT 0, expected_return_rate REAL DEFAULT 6,
        inflation_rate REAL DEFAULT 3.5, total_projected REAL DEFAULT 0,
        shortfall REAL DEFAULT 0, inflated_monthly_expenses REAL DEFAULT 0,
        progress_percent INTEGER DEFAULT 0,
        created_at TEXT DEFAULT CURRENT_TIMESTAMP,
        updated_at TEXT DEFAULT CURRENT_TIMESTAMP
      )`,
      `CREATE TABLE IF NOT EXISTS financial_goals (
        id TEXT PRIMARY KEY, user_id TEXT NOT NULL,
        title TEXT NOT NULL, description TEXT,
        target_amount REAL DEFAULT 0, current_amount REAL DEFAULT 0,
        category TEXT DEFAULT 'general', priority TEXT DEFAULT 'medium',
        status TEXT DEFAULT 'active',
        created_at TEXT DEFAULT CURRENT_TIMESTAMP,
        updated_at TEXT DEFAULT CURRENT_TIMESTAMP
      )`,
      `CREATE TABLE IF NOT EXISTS zakat_records (
        id TEXT PRIMARY KEY, user_id TEXT NOT NULL, year INTEGER NOT NULL,
        cash REAL DEFAULT 0, investments REAL DEFAULT 0,
        gold_value REAL DEFAULT 0, silver_value REAL DEFAULT 0,
        business_assets REAL DEFAULT 0, receivables REAL DEFAULT 0,
        deductible_liabilities REAL DEFAULT 0, total_wealth REAL NOT NULL,
        nisab_threshold REAL NOT NULL, zakat_payable REAL NOT NULL,
        created_at TEXT DEFAULT CURRENT_TIMESTAMP,
        updated_at TEXT DEFAULT CURRENT_TIMESTAMP,
        UNIQUE(user_id, year)
      )`,
      `CREATE TABLE IF NOT EXISTS hajj_plans (
        id TEXT PRIMARY KEY, user_id TEXT NOT NULL,
        target_year INTEGER NOT NULL, estimated_cost REAL DEFAULT 0,
        monthly_saving REAL DEFAULT 0, total_saved REAL DEFAULT 0,
        progress_percent REAL DEFAULT 0, package_type TEXT DEFAULT 'standard',
        travelers INTEGER DEFAULT 1,
        created_at TEXT DEFAULT CURRENT_TIMESTAMP,
        updated_at TEXT DEFAULT CURRENT_TIMESTAMP,
        UNIQUE(user_id, target_year)
      )`,
      // NEW TABLES
      `CREATE TABLE IF NOT EXISTS transactions (
        id TEXT PRIMARY KEY, user_id TEXT NOT NULL,
        type TEXT NOT NULL, category TEXT NOT NULL,
        amount REAL DEFAULT 0, description TEXT,
        date TEXT DEFAULT CURRENT_TIMESTAMP,
        is_recurring INTEGER DEFAULT 0,
        created_at TEXT DEFAULT CURRENT_TIMESTAMP
      )`,
      `CREATE TABLE IF NOT EXISTS assets (
        id TEXT PRIMARY KEY, user_id TEXT NOT NULL,
        name TEXT NOT NULL, type TEXT NOT NULL,
        value REAL DEFAULT 0, current_value REAL DEFAULT 0,
        is_active INTEGER DEFAULT 1,
        created_at TEXT DEFAULT CURRENT_TIMESTAMP
      )`,
      `CREATE TABLE IF NOT EXISTS liabilities (
        id TEXT PRIMARY KEY, user_id TEXT NOT NULL,
        name TEXT NOT NULL, type TEXT NOT NULL,
        original_amount REAL DEFAULT 0, remaining_amount REAL DEFAULT 0,
        monthly_payment REAL DEFAULT 0, interest_rate REAL DEFAULT 0,
        is_active INTEGER DEFAULT 1,
        created_at TEXT DEFAULT CURRENT_TIMESTAMP
      )`,
      `CREATE TABLE IF NOT EXISTS chat_sessions (
        id TEXT PRIMARY KEY, user_id TEXT NOT NULL,
        title TEXT, created_at TEXT DEFAULT CURRENT_TIMESTAMP,
        updated_at TEXT DEFAULT CURRENT_TIMESTAMP
      )`,
      `CREATE TABLE IF NOT EXISTS chat_messages (
        id TEXT PRIMARY KEY, user_id TEXT NOT NULL,
        session_id TEXT, role TEXT NOT NULL,
        content TEXT NOT NULL, created_at TEXT DEFAULT CURRENT_TIMESTAMP
      )`,
      `CREATE TABLE IF NOT EXISTS advisors (
        id TEXT PRIMARY KEY, email TEXT UNIQUE, full_name TEXT,
        credentials TEXT, specializations TEXT, bio TEXT,
        rating REAL DEFAULT 0, phone TEXT, consultation_fee REAL DEFAULT 0,
        is_active INTEGER DEFAULT 1
      )`,
      `CREATE TABLE IF NOT EXISTS advisor_bookings (
        id TEXT PRIMARY KEY, user_id TEXT NOT NULL,
        advisor_id TEXT NOT NULL, scheduled_at TEXT,
        status TEXT DEFAULT 'PENDING', notes TEXT,
        created_at TEXT DEFAULT CURRENT_TIMESTAMP
      )`,
      `CREATE TABLE IF NOT EXISTS notifications (
        id TEXT PRIMARY KEY, user_id TEXT NOT NULL,
        type TEXT, title TEXT, message TEXT,
        is_read INTEGER DEFAULT 0,
        created_at TEXT DEFAULT CURRENT_TIMESTAMP
      )`,
      `CREATE TABLE IF NOT EXISTS ai_usage (
        id TEXT PRIMARY KEY, user_id TEXT NOT NULL,
        date TEXT NOT NULL, message_count INTEGER DEFAULT 0,
        UNIQUE(user_id, date)
      )`,
      `CREATE TABLE IF NOT EXISTS reminders (
        id TEXT PRIMARY KEY, user_id TEXT NOT NULL,
        type TEXT DEFAULT 'general', title TEXT,
        message TEXT, scheduled_date TEXT,
        is_completed INTEGER DEFAULT 0, completed_date TEXT,
        created_at TEXT DEFAULT CURRENT_TIMESTAMP
      )`,
    ];

    for (const stmt of schema) {
      try { await this.db!.execute(stmt); } catch (e) { console.log('Schema note:', e); }
    }
    await this.migrateRetirementTable();
  }

  private async migrateRetirementTable(): Promise<void> {
    const columns = [
      { name: 'monthly_expenses', type: 'REAL DEFAULT 0' },
      { name: 'expected_return_rate', type: 'REAL DEFAULT 6' },
      { name: 'inflation_rate', type: 'REAL DEFAULT 3.5' },
      { name: 'total_projected', type: 'REAL DEFAULT 0' },
      { name: 'shortfall', type: 'REAL DEFAULT 0' },
      { name: 'inflated_monthly_expenses', type: 'REAL DEFAULT 0' },
      { name: 'progress_percent', type: 'INTEGER DEFAULT 0' },
    ];

    for (const col of columns) {
      try {
        await this.db!.execute(`ALTER TABLE retirement_plans ADD COLUMN ${col.name} ${col.type}`);
        console.log(`✅ Added column: ${col.name}`);
      } catch (e: any) {
        if (e.message && e.message.includes('duplicate column')) {
          console.log(`ℹ️ Column ${col.name} already exists`);
        } else {
          console.log(`⚠️ Migration note for ${col.name}:`, e);
        }
      }
    }
  }

  private async seedAdvisors(): Promise<void> {
    const existing = await this.db!.query('SELECT COUNT(*) as cnt FROM advisors');
    if (existing?.values?.[0]?.cnt > 0) return;

    const advisors = [
      ['adv-001', 'ahmad@nurwallet.local', 'Ahmad bin Abdullah', 'Certified Shariah Financial Advisor',
       JSON.stringify(['Zakat', 'Retirement', 'Takaful']), '15 years in Islamic finance.', 4.9, '+60123456789', 150],
      ['adv-002', 'siti@nurwallet.local', 'Siti Nurhaliza', 'Islamic Estate Planning Specialist',
       JSON.stringify(['Estate Planning', 'Hibah', 'Wasiat']), 'Expert in Islamic inheritance law.', 4.8, '+60198765432', 200],
      ['adv-003', 'khalid@nurwallet.local', 'Khalid Ibrahim', 'Takaful & Risk Management Expert',
       JSON.stringify(['Takaful', 'Risk Management', 'Medical']), 'Former Takaful actuary.', 4.7, '+60187654321', 120],
    ];

    for (const a of advisors) {
      await this.db!.run(
        `INSERT INTO advisors (id, email, full_name, credentials, specializations, bio, rating, phone, consultation_fee, is_active)
         VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, 1)`, a
      );
    }
  }

  private generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  // USER METHODS
  async createUser(userData: any): Promise<any> {
    await this.init();
    const id = this.generateId();
    const now = new Date().toISOString();
    await this.db!.run(
      `INSERT INTO users (id, email, password, full_name, family_status, tier, created_at, updated_at) VALUES (?, ?, ?, ?, ?, ?, ?, ?)`,
      [id, userData.email, userData.password, userData.fullName || userData.full_name, userData.familyStatus || userData.family_status || 'SINGLE', 'free', now, now]
    );
    return this.getUserById(id);
  }

  async getUserById(id: string): Promise<any> {
    await this.init();
    const res = await this.db!.query('SELECT * FROM users WHERE id = ?', [id]);
    if (!res.values || res.values.length === 0) throw new Error('User not found');
    return this.mapUser(res.values[0]);
  }

  async getUserByEmail(email: string): Promise<any | null> {
    await this.init();
    const res = await this.db!.query('SELECT * FROM users WHERE email = ?', [email]);
    if (!res.values || res.values.length === 0) return null;
    return this.mapUser(res.values[0]);
  }

  async getAllUsers(): Promise<any[]> {
    await this.init();
    const res = await this.db!.query('SELECT * FROM users');
    return (res.values || []).map((row: any) => this.mapUser(row));
  }

  async updateUser(id: string, updates: any): Promise<void> {
    await this.init();
    const fields = Object.keys(updates).filter(k => k !== 'id');
    if (fields.length === 0) return;
    const setClause = fields.map(k => `${k.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`)} = ?`).join(', ');
    const values = fields.map(k => updates[k]);
    await this.db!.run(`UPDATE users SET ${setClause}, updated_at = ? WHERE id = ?`, [...values, new Date().toISOString(), id]);
  }

  // TAKAFUL METHODS
  async saveTakaful(data: any): Promise<any> {
    await this.init();
    const now = new Date().toISOString();
    const existing = await this.db!.query('SELECT id FROM takaful_plans WHERE user_id = ?', [data.userId]);

    if (existing?.values && existing.values.length > 0) {
      await this.db!.run(
        `UPDATE takaful_plans SET annual_income = ?, outstanding_debts = ?, education_fund = ?,
          funeral_expenses = ?, emergency_fund_months = ?, dependents = ?,
          existing_coverage = ?, recommended_coverage = ?, premium_estimate = ?, updated_at = ?
        WHERE user_id = ?`,
        [data.annualIncome, data.outstandingDebts, data.educationFund, data.funeralExpenses,
         data.emergencyFundMonths, data.dependents, data.existingCoverage,
         data.recommendedCoverage, data.premiumEstimate, now, data.userId]
      );
      return { ...data, updatedAt: now };
    } else {
      const id = this.generateId();
      await this.db!.run(
        `INSERT INTO takaful_plans (id, user_id, annual_income, outstanding_debts, education_fund,
          funeral_expenses, emergency_fund_months, dependents, existing_coverage,
          recommended_coverage, premium_estimate, created_at, updated_at)
        VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [id, data.userId, data.annualIncome, data.outstandingDebts, data.educationFund,
         data.funeralExpenses, data.emergencyFundMonths, data.dependents,
         data.existingCoverage, data.recommendedCoverage, data.premiumEstimate, now, now]
      );
      return { ...data, id, createdAt: now, updatedAt: now };
    }
  }

  async getTakafulPlan(userId: string): Promise<any | null> {
    await this.init();
    const res = await this.db!.query('SELECT * FROM takaful_plans WHERE user_id = ?', [userId]);
    if (!res.values || res.values.length === 0) return null;
    const row = res.values[0];
    return {
      id: row.id, userId: row.user_id, annualIncome: row.annual_income,
      outstandingDebts: row.outstanding_debts, educationFund: row.education_fund,
      funeralExpenses: row.funeral_expenses, emergencyFundMonths: row.emergency_fund_months,
      dependents: row.dependents, existingCoverage: row.existing_coverage,
      recommendedCoverage: row.recommended_coverage, premiumEstimate: row.premium_estimate,
      createdAt: row.created_at, updatedAt: row.updated_at,
    };
  }

  // RETIREMENT METHODS
  async saveRetirement(data: any): Promise<any> {
    await this.init();
    const now = new Date().toISOString();

    try {
      const existing = await this.db!.query('SELECT id FROM retirement_plans WHERE user_id = ?', [data.userId]);
      console.log('[DB] saveRetirement - existing check:', existing);

      if (existing?.values && existing.values.length > 0) {
        console.log('[DB] Updating existing retirement plan for user:', data.userId);
        const updateResult = await this.db!.run(
          `UPDATE retirement_plans SET current_age = ?, retirement_age = ?, life_expectancy = ?,
            current_savings = ?, monthly_contribution = ?, target_amount = ?,
            monthly_expenses = ?, expected_return_rate = ?, inflation_rate = ?,
            total_projected = ?, shortfall = ?, inflated_monthly_expenses = ?, progress_percent = ?, updated_at = ?
          WHERE user_id = ?`,
          [data.currentAge, data.retirementAge, data.lifeExpectancy,
           data.currentSavings, data.monthlyContribution, data.targetAmount,
           data.monthlyExpenses || 0, data.expectedReturnRate || 6, data.inflationRate || 3.5,
           data.totalProjected || 0, data.shortfall || 0, data.inflatedMonthlyExpenses || 0,
           data.progressPercent || 0, now, data.userId]
        );
        console.log('[DB] Update result:', updateResult);
        return { ...data, updatedAt: now };
      } else {
        console.log('[DB] Inserting new retirement plan for user:', data.userId);
        const id = this.generateId();
        const insertResult = await this.db!.run(
          `INSERT INTO retirement_plans (id, user_id, current_age, retirement_age, life_expectancy,
            current_savings, monthly_contribution, target_amount, monthly_expenses,
            expected_return_rate, inflation_rate, total_projected, shortfall,
            inflated_monthly_expenses, progress_percent, created_at, updated_at)
          VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
          [id, data.userId, data.currentAge, data.retirementAge, data.lifeExpectancy,
           data.currentSavings, data.monthlyContribution, data.targetAmount,
           data.monthlyExpenses || 0, data.expectedReturnRate || 6, data.inflationRate || 3.5,
           data.totalProjected || 0, data.shortfall || 0, data.inflatedMonthlyExpenses || 0,
           data.progressPercent || 0, now, now]
        );
        console.log('[DB] Insert result:', insertResult);
        return { ...data, id, createdAt: now, updatedAt: now };
      }
    } catch (error: any) {
      console.error('[DB] saveRetirement ERROR:', error);
      // If error is about missing columns, try recreating the table
      if (error.message && (error.message.includes('no such column') || error.message.includes('has no column'))) {
        console.log('[DB] Attempting table recreation...');
        await this.recreateRetirementTable();
        // Retry once
        return this.saveRetirement(data);
      }
      throw error;
    }
  }

  private async recreateRetirementTable(): Promise<void> {
    console.log('[DB] Dropping and recreating retirement_plans table...');
    try {
      await this.db!.execute('DROP TABLE IF EXISTS retirement_plans');
    } catch (e) { console.log('[DB] Drop error (ignored):', e); }

    await this.db!.execute(`CREATE TABLE IF NOT EXISTS retirement_plans (
      id TEXT PRIMARY KEY, user_id TEXT NOT NULL UNIQUE,
      current_age INTEGER DEFAULT 30, retirement_age INTEGER DEFAULT 60,
      life_expectancy INTEGER DEFAULT 80, current_savings REAL DEFAULT 0,
      monthly_contribution REAL DEFAULT 0, target_amount REAL DEFAULT 0,
      monthly_expenses REAL DEFAULT 0, expected_return_rate REAL DEFAULT 6,
      inflation_rate REAL DEFAULT 3.5, total_projected REAL DEFAULT 0,
      shortfall REAL DEFAULT 0, inflated_monthly_expenses REAL DEFAULT 0,
      progress_percent INTEGER DEFAULT 0,
      created_at TEXT DEFAULT CURRENT_TIMESTAMP,
      updated_at TEXT DEFAULT CURRENT_TIMESTAMP
    )`);
    console.log('[DB] Table recreated successfully');
  }

  async getRetirementPlan(userId: string): Promise<any | null> {
    await this.init();
    const res = await this.db!.query('SELECT * FROM retirement_plans WHERE user_id = ?', [userId]);
    if (!res.values || res.values.length === 0) return null;
    const row = res.values[0];
    return {
      id: row.id, userId: row.user_id, currentAge: row.current_age,
      retirementAge: row.retirement_age, lifeExpectancy: row.life_expectancy,
      currentSavings: row.current_savings, monthlyContribution: row.monthly_contribution,
      targetAmount: row.target_amount, monthlyExpenses: row.monthly_expenses,
      expectedReturnRate: row.expected_return_rate, inflationRate: row.inflation_rate,
      totalProjected: row.total_projected, shortfall: row.shortfall,
      inflatedMonthlyExpenses: row.inflated_monthly_expenses, progressPercent: row.progress_percent,
      createdAt: row.created_at, updatedAt: row.updated_at,
    };
  }

  // GOALS METHODS
  async createGoal(userId: string, goal: any): Promise<any> {
    await this.init();
    const now = new Date().toISOString();
    const id = this.generateId();
    await this.db!.run(
      `INSERT INTO financial_goals (id, user_id, title, description, target_amount, current_amount,
        category, priority, status, created_at, updated_at) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
      [id, userId, goal.title, goal.description || null, goal.targetAmount || 0,
       goal.currentAmount || 0, goal.category || 'general', goal.priority || 'medium',
       goal.status || 'active', now, now]
    );
    return { ...goal, id, userId, createdAt: now, updatedAt: now };
  }

  // ZAKAT METHODS
  async saveZakat(data: any): Promise<any> {
    await this.init();
    const now = new Date().toISOString();
    const existing = await this.db!.query('SELECT id FROM zakat_records WHERE user_id = ? AND year = ?', [data.userId, data.year]);

    if (existing?.values && existing.values.length > 0) {
      await this.db!.run(
        `UPDATE zakat_records SET cash = ?, investments = ?, gold_value = ?, silver_value = ?,
          business_assets = ?, receivables = ?, deductible_liabilities = ?, total_wealth = ?,
          nisab_threshold = ?, zakat_payable = ?, updated_at = ?
        WHERE user_id = ? AND year = ?`,
        [data.cash, data.investments, data.goldValue, data.silverValue,
         data.businessAssets, data.receivables, data.deductibleLiabilities,
         data.totalWealth, data.nisabThreshold, data.zakatPayable, now, data.userId, data.year]
      );
      return { ...data, updatedAt: now };
    } else {
      const id = this.generateId();
      await this.db!.run(
        `INSERT INTO zakat_records (id, user_id, year, cash, investments, gold_value, silver_value,
          business_assets, receivables, deductible_liabilities, total_wealth, nisab_threshold,
          zakat_payable, created_at, updated_at) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [id, data.userId, data.year, data.cash, data.investments, data.goldValue, data.silverValue,
         data.businessAssets, data.receivables, data.deductibleLiabilities,
         data.totalWealth, data.nisabThreshold, data.zakatPayable, now, now]
      );
      return { ...data, id, createdAt: now, updatedAt: now };
    }
  }

  async getZakatRecords(userId: string): Promise<any[]> {
    await this.init();
    const res = await this.db!.query('SELECT * FROM zakat_records WHERE user_id = ? ORDER BY year DESC', [userId]);
    return (res.values || []).map((row: any) => ({
      id: row.id, userId: row.user_id, year: row.year, cash: row.cash,
      investments: row.investments, goldValue: row.gold_value, silverValue: row.silver_value,
      businessAssets: row.business_assets, receivables: row.receivables,
      deductibleLiabilities: row.deductible_liabilities, totalWealth: row.total_wealth,
      nisabThreshold: row.nisab_threshold, zakatPayable: row.zakat_payable,
      createdAt: row.created_at, updatedAt: row.updated_at,
    }));
  }

  // HAJJ METHODS
  async saveHajj(data: any): Promise<any> {
    await this.init();
    const now = new Date().toISOString();
    const existing = await this.db!.query('SELECT id FROM hajj_plans WHERE user_id = ? AND target_year = ?', [data.userId, data.targetYear]);

    if (existing?.values && existing.values.length > 0) {
      await this.db!.run(
        `UPDATE hajj_plans SET estimated_cost = ?, monthly_saving = ?, total_saved = ?,
          progress_percent = ?, package_type = ?, travelers = ?, updated_at = ?
        WHERE user_id = ? AND target_year = ?`,
        [data.estimatedCost, data.monthlySaving, data.totalSaved, data.progressPercent,
         data.packageType, data.travelers, now, data.userId, data.targetYear]
      );
      return { ...data, updatedAt: now };
    } else {
      const id = this.generateId();
      await this.db!.run(
        `INSERT INTO hajj_plans (id, user_id, target_year, estimated_cost, monthly_saving,
          total_saved, progress_percent, package_type, travelers, created_at, updated_at)
        VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [id, data.userId, data.targetYear, data.estimatedCost, data.monthlySaving,
         data.totalSaved, data.progressPercent, data.packageType, data.travelers, now, now]
      );
      return { ...data, id, createdAt: now, updatedAt: now };
    }
  }

  async getHajjPlans(userId: string): Promise<any[]> {
    await this.init();
    const res = await this.db!.query('SELECT * FROM hajj_plans WHERE user_id = ? ORDER BY target_year DESC', [userId]);
    return (res.values || []).map((row: any) => ({
      id: row.id, userId: row.user_id, targetYear: row.target_year,
      estimatedCost: row.estimated_cost, monthlySaving: row.monthly_saving,
      totalSaved: row.total_saved, progressPercent: row.progress_percent,
      packageType: row.package_type, travelers: row.travelers,
      createdAt: row.created_at, updatedAt: row.updated_at,
    }));
  }

  // ============================================================
  // TRANSACTION METHODS
  // ============================================================
  async createTransaction(data: any): Promise<any> {
    await this.init();
    const id = this.generateId();
    const now = new Date().toISOString();
    await this.db!.run(
      `INSERT INTO transactions (id, user_id, type, category, amount, description, date, is_recurring, created_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)`,
      [id, data.userId, data.type, data.category, data.amount || 0,
       data.description || null, data.date || now, data.isRecurring ? 1 : 0, now]
    );
    return { id, ...data, createdAt: now };
  }

  async getTransactions(userId: string, filters?: any): Promise<any[]> {
    await this.init();
    let query = 'SELECT * FROM transactions WHERE user_id = ?';
    const params: any[] = [userId];
    if (filters?.type) {
      query += ' AND type = ?';
      params.push(filters.type);
    }
    query += ' ORDER BY date DESC';
    const res = await this.db!.query(query, params);
    return (res.values || []).map((row: any) => ({
      id: row.id, type: row.type, category: row.category, amount: row.amount,
      description: row.description, date: row.date, isRecurring: row.is_recurring === 1,
      createdAt: row.created_at,
    }));
  }

  async getTransactionSummary(userId: string): Promise<any> {
    await this.init();
    const res = await this.db!.query(
      `SELECT type, SUM(amount) as total FROM transactions WHERE user_id = ? GROUP BY type`, [userId]
    );
    let income = 0, expense = 0;
    (res.values || []).forEach((row: any) => {
      if (row.type === 'INCOME') income = row.total || 0;
      if (row.type === 'EXPENSE') expense = row.total || 0;
    });
    return { totalIncome: income, totalExpense: expense, net: income - expense };
  }

  async deleteTransaction(id: string, userId: string): Promise<void> {
    await this.init();
    await this.db!.run('DELETE FROM transactions WHERE id = ? AND user_id = ?', [id, userId]);
  }

  // ============================================================
  // ASSET METHODS
  // ============================================================
  async createAsset(data: any): Promise<any> {
    await this.init();
    const id = this.generateId();
    const now = new Date().toISOString();
    await this.db!.run(
      `INSERT INTO assets (id, user_id, name, type, value, current_value, is_active, created_at)
       VALUES (?, ?, ?, ?, ?, ?, 1, ?)`,
      [id, data.userId, data.name, data.type, data.value || 0, data.value || 0, now]
    );
    return { id, ...data, currentValue: data.value, isActive: true, createdAt: now };
  }

  async getAssets(userId: string): Promise<any[]> {
    await this.init();
    const res = await this.db!.query(
      'SELECT * FROM assets WHERE user_id = ? AND is_active = 1 ORDER BY created_at DESC', [userId]
    );
    return (res.values || []).map((row: any) => ({
      id: row.id, name: row.name, type: row.type, value: row.value,
      currentValue: row.current_value, isActive: row.is_active === 1, createdAt: row.created_at,
    }));
  }

  async getAssetSummary(userId: string): Promise<any> {
    await this.init();
    const res = await this.db!.query(
      'SELECT type, SUM(current_value) as total FROM assets WHERE user_id = ? AND is_active = 1 GROUP BY type', [userId]
    );
    const byType: Record<string, number> = {};
    let total = 0;
    (res.values || []).forEach((row: any) => {
      byType[row.type] = row.total || 0;
      total += row.total || 0;
    });
    return { totalValue: total, byType };
  }

  async deleteAsset(id: string, userId: string): Promise<void> {
    await this.init();
    await this.db!.run(
      'UPDATE assets SET is_active = 0 WHERE id = ? AND user_id = ?', [id, userId]
    );
  }

  // ============================================================
  // LIABILITY METHODS
  // ============================================================
  async createLiability(data: any): Promise<any> {
    await this.init();
    const id = this.generateId();
    const now = new Date().toISOString();
    await this.db!.run(
      `INSERT INTO liabilities (id, user_id, name, type, original_amount, remaining_amount, monthly_payment, interest_rate, is_active, created_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, 1, ?)`,
      [id, data.userId, data.name, data.type, data.originalAmount || 0,
       data.remainingAmount || 0, data.monthlyPayment || 0, data.interestRate || 0, now]
    );
    return { id, ...data, isActive: true, createdAt: now };
  }

  async getLiabilities(userId: string): Promise<any[]> {
    await this.init();
    const res = await this.db!.query(
      'SELECT * FROM liabilities WHERE user_id = ? AND is_active = 1', [userId]
    );
    return (res.values || []).map((row: any) => ({
      id: row.id, name: row.name, type: row.type, originalAmount: row.original_amount,
      remainingAmount: row.remaining_amount, monthlyPayment: row.monthly_payment,
      interestRate: row.interest_rate, isActive: row.is_active === 1, createdAt: row.created_at,
    }));
  }

  async getLiabilitySummary(userId: string): Promise<any> {
    await this.init();
    const res = await this.db!.query(
      `SELECT type, SUM(remaining_amount) as total_remaining, SUM(monthly_payment) as total_monthly
       FROM liabilities WHERE user_id = ? AND is_active = 1 GROUP BY type`, [userId]
    );
    const byType: Record<string, number> = {};
    let totalRemaining = 0;
    let monthlyPayments = 0;
    (res.values || []).forEach((row: any) => {
      byType[row.type] = row.total_remaining || 0;
      totalRemaining += row.total_remaining || 0;
      monthlyPayments += row.total_monthly || 0;
    });
    return { totalRemaining, monthlyPayments, byType };
  }

  async deleteLiability(id: string, userId: string): Promise<void> {
    await this.init();
    await this.db!.run(
      'UPDATE liabilities SET is_active = 0 WHERE id = ? AND user_id = ?', [id, userId]
    );
  }

  // ============================================================
  // CHAT METHODS
  // ============================================================
  async createChatSession(userId: string, title: string): Promise<any> {
    await this.init();
    const id = this.generateId();
    const now = new Date().toISOString();
    await this.db!.run(
      `INSERT INTO chat_sessions (id, user_id, title, created_at, updated_at) VALUES (?, ?, ?, ?, ?)`,
      [id, userId, title, now, now]
    );
    return { id, userId, title, createdAt: now, updatedAt: now };
  }

  async getChatSessions(userId: string): Promise<any[]> {
    await this.init();
    const res = await this.db!.query(
      'SELECT * FROM chat_sessions WHERE user_id = ? ORDER BY updated_at DESC', [userId]
    );
    return (res.values || []).map((row: any) => ({
      id: row.id, userId: row.user_id, title: row.title,
      createdAt: row.created_at, updatedAt: row.updated_at,
    }));
  }

  async createChatMessage(data: any): Promise<any> {
    await this.init();
    const id = this.generateId();
    const now = new Date().toISOString();
    await this.db!.run(
      `INSERT INTO chat_messages (id, user_id, session_id, role, content, created_at) VALUES (?, ?, ?, ?, ?, ?)`,
      [id, data.userId, data.sessionId || null, data.role, data.content, now]
    );
    if (data.sessionId) {
      await this.db!.run(
        'UPDATE chat_sessions SET updated_at = ? WHERE id = ?', [now, data.sessionId]
      );
    }
    return { id, ...data, createdAt: now };
  }

  async getChatMessages(userId: string, sessionId?: string): Promise<any[]> {
    await this.init();
    let query = 'SELECT * FROM chat_messages WHERE user_id = ?';
    const params: any[] = [userId];
    if (sessionId) {
      query += ' AND session_id = ?';
      params.push(sessionId);
    }
    query += ' ORDER BY created_at ASC';
    const res = await this.db!.query(query, params);
    return (res.values || []).map((row: any) => ({
      id: row.id, userId: row.user_id, sessionId: row.session_id,
      role: row.role, content: row.content, createdAt: row.created_at,
    }));
  }

  // ============================================================
  // ADVISOR METHODS
  // ============================================================
  async getAdvisors(): Promise<any[]> {
    await this.init();
    const res = await this.db!.query(
      'SELECT * FROM advisors WHERE is_active = 1 ORDER BY rating DESC'
    );
    return (res.values || []).map((row: any) => ({
      id: row.id, email: row.email, fullName: row.full_name,
      credentials: row.credentials, specializations: row.specializations,
      bio: row.bio, rating: row.rating, phone: row.phone,
      consultationFee: row.consultation_fee, isActive: row.is_active === 1,
    }));
  }

  async createAdvisorBooking(data: any): Promise<any> {
    await this.init();
    const id = this.generateId();
    const now = new Date().toISOString();
    await this.db!.run(
      `INSERT INTO advisor_bookings (id, user_id, advisor_id, scheduled_at, status, notes, created_at)
       VALUES (?, ?, ?, ?, 'PENDING', ?, ?)`,
      [id, data.userId, data.advisorId, data.scheduledAt, data.notes || null, now]
    );
    return { id, ...data, status: 'PENDING', createdAt: now };
  }

  async getAdvisorBookings(userId: string): Promise<any[]> {
    await this.init();
    const res = await this.db!.query(
      'SELECT * FROM advisor_bookings WHERE user_id = ? ORDER BY scheduled_at DESC', [userId]
    );
    return (res.values || []).map((row: any) => ({
      id: row.id, userId: row.user_id, advisorId: row.advisor_id,
      scheduledAt: row.scheduled_at, status: row.status,
      notes: row.notes, createdAt: row.created_at,
    }));
  }

  // ============================================================
  // NOTIFICATION METHODS
  // ============================================================
  async createNotification(data: any): Promise<any> {
    await this.init();
    const id = this.generateId();
    const now = new Date().toISOString();
    await this.db!.run(
      `INSERT INTO notifications (id, user_id, type, title, message, is_read, created_at)
       VALUES (?, ?, ?, ?, ?, 0, ?)`,
      [id, data.userId, data.type, data.title, data.message, now]
    );
    return { id, ...data, isRead: false, createdAt: now };
  }

  async getNotifications(userId: string): Promise<any[]> {
    await this.init();
    const res = await this.db!.query(
      'SELECT * FROM notifications WHERE user_id = ? ORDER BY created_at DESC', [userId]
    );
    return (res.values || []).map((row: any) => ({
      id: row.id, userId: row.user_id, type: row.type,
      title: row.title, message: row.message, isRead: row.is_read === 1,
      createdAt: row.created_at,
    }));
  }

  async markNotificationRead(id: string): Promise<void> {
    await this.init();
    await this.db!.run('UPDATE notifications SET is_read = 1 WHERE id = ?', [id]);
  }

  async getUnreadCount(userId: string): Promise<number> {
    await this.init();
    const res = await this.db!.query(
      'SELECT COUNT(*) as cnt FROM notifications WHERE user_id = ? AND is_read = 0', [userId]
    );
    return res.values?.[0]?.cnt || 0;
  }

  // ============================================================
  // AI USAGE METHODS
  // ============================================================
  async getTodayAIUsage(userId: string): Promise<number> {
    await this.init();
    const today = new Date().toISOString().split('T')[0];
    const res = await this.db!.query(
      'SELECT message_count FROM ai_usage WHERE user_id = ? AND date = ?', [userId, today]
    );
    return res.values?.[0]?.message_count || 0;
  }

  async incrementAIUsage(userId: string): Promise<void> {
    await this.init();
    const today = new Date().toISOString().split('T')[0];
    const existing = await this.db!.query(
      'SELECT id FROM ai_usage WHERE user_id = ? AND date = ?', [userId, today]
    );
    if (existing?.values && existing.values.length > 0) {
      await this.db!.run(
        'UPDATE ai_usage SET message_count = message_count + 1 WHERE user_id = ? AND date = ?',
        [userId, today]
      );
    } else {
      const id = this.generateId();
      await this.db!.run(
        'INSERT INTO ai_usage (id, user_id, date, message_count) VALUES (?, ?, ?, 1)',
        [id, userId, today]
      );
    }
  }

  // ============================================================
  // STRUCTURED DATA METHODS (for AI)
  // ============================================================
  async getAssetsStructured(userId: string): Promise<any> {
    const summary = await this.getAssetSummary(userId);
    return {
      userId, cash: summary.byType['cash'] || 0, bankAccounts: summary.byType['bank'] || 0,
      goldValue: summary.byType['gold'] || 0, investments: summary.byType['investment'] || 0,
      stocks: summary.byType['stocks'] || 0, unitTrust: summary.byType['unit_trust'] || 0,
      sukuk: summary.byType['sukuk'] || 0, reits: summary.byType['reits'] || 0,
      tabungHaji: summary.byType['tabung_haji'] || 0, epf: summary.byType['epf'] || 0,
      prs: summary.byType['prs'] || 0, propertyValue: summary.byType['property'] || 0,
      businessInventory: summary.byType['business'] || 0, receivables: summary.byType['receivables'] || 0,
      otherAssets: summary.byType['other'] || 0, total: summary.totalValue,
      updatedAt: new Date().toISOString(),
    };
  }

  async getLiabilitiesStructured(userId: string): Promise<any> {
    const summary = await this.getLiabilitySummary(userId);
    return {
      userId, homeLoan: summary.byType['home'] || 0, carLoan: summary.byType['car'] || 0,
      personalLoan: summary.byType['personal'] || 0, creditCard: summary.byType['credit_card'] || 0,
      ptptn: summary.byType['ptptn'] || 0, otherDebts: summary.byType['other'] || 0,
      total: summary.totalRemaining, monthlyPayments: summary.monthlyPayments,
      interestRate: 0, updatedAt: new Date().toISOString(),
    };
  }

  async getZakatSummary(userId: string): Promise<any> {
    const records = await this.getZakatRecords(userId);
    const currentYear = new Date().getFullYear();
    const totalPending = records.filter((z: any) => z.zakatPayable > 0).reduce((sum: number, z: any) => sum + (z.zakatPayable || 0), 0);
    const currentRecord = records.find((z: any) => z.year === currentYear);
    return {
      totalPaid: 0, totalPending, lastPaidYear: records.length > 0 ? records[0].year : null,
      lastPaidAmount: 0, currentLiability: currentRecord?.zakatPayable || 0,
      isLiable: (currentRecord?.zakatPayable || 0) > 0,
    };
  }

  async getIncomeExpenseSummary(userId: string): Promise<any> {
    const txSummary = await this.getTransactionSummary(userId);
    const transactions = await this.getTransactions(userId);
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    const recentTx = transactions.filter((t: any) => new Date(t.date) >= thirtyDaysAgo);
    const incomeBreakdown: Record<string, number> = {};
    const expenseBreakdown: Record<string, number> = {};
    for (const t of recentTx) {
      if (t.type === 'INCOME') incomeBreakdown[t.category] = (incomeBreakdown[t.category] || 0) + t.amount;
      else expenseBreakdown[t.category] = (expenseBreakdown[t.category] || 0) + t.amount;
    }
    return {
      monthlyIncome: txSummary.totalIncome, monthlyExpenses: txSummary.totalExpense,
      monthlySavings: txSummary.net, incomeBreakdown, expenseBreakdown,
    };
  }

  async getNisabThreshold(): Promise<any> {
    const currentYear = new Date().getFullYear();
    return {
      year: currentYear, goldPricePerGram: 330, nisabGrams: 85,
      amount: 28050, effectiveDate: new Date().toISOString(), source: 'Default',
    };
  }

  // ============================================================
  // RETIREMENT UPDATE METHOD
  // ============================================================
  async updateRetirementPlan(userId: string, updates: any): Promise<void> {
    await this.init();
    const fields: Record<string, string> = {
      currentAge: 'current_age', retirementAge: 'retirement_age',
      lifeExpectancy: 'life_expectancy', currentSavings: 'current_savings',
      monthlyContribution: 'monthly_contribution', targetAmount: 'target_amount',
    };
    const setParts: string[] = [];
    const values: any[] = [];
    Object.entries(updates).forEach(([key, value]) => {
      if (fields[key] && value !== undefined) {
        setParts.push(`${fields[key]} = ?`);
        values.push(value);
      }
    });
    if (setParts.length === 0) return;
    setParts.push('updated_at = ?');
    values.push(new Date().toISOString());
    values.push(userId);
    await this.db!.run(
      `UPDATE retirement_plans SET ${setParts.join(', ')} WHERE user_id = ?`, values
    );
  }

  // ============================================================
  // REMINDER METHODS
  // ============================================================
  async createReminder(userId: string, reminder: any): Promise<any> {
    await this.init();
    const id = this.generateId();
    const now = new Date().toISOString();
    await this.db!.run(
      `INSERT INTO reminders (id, user_id, type, title, message, scheduled_date, is_completed, completed_date, created_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)`,
      [id, userId, reminder.type || 'general', reminder.title,
       reminder.message || null, reminder.scheduledDate || null,
       reminder.isCompleted ? 1 : 0, reminder.completedDate || null, now]
    );
    return { ...reminder, id, userId, createdAt: now };
  }

  // ============================================================
  // COMPATIBILITY METHODS
  // ============================================================
  async createZakatRecord(data: any): Promise<any> {
    return this.saveZakat({
      userId: data.userId, year: data.year, cash: Number(data.cashOnHand) || 0,
      investments: Number(data.investments) || 0, goldValue: Number(data.goldValue) || 0,
      silverValue: Number(data.silverValue) || 0, businessAssets: Number(data.businessAssets) || 0,
      receivables: Number(data.receivables) || 0, deductibleLiabilities: Number(data.liabilities) || 0,
      totalWealth: data.totalWealth, nisabThreshold: data.nisabThreshold, zakatPayable: data.zakatPayable,
    });
  }

  async getZakatByYear(userId: string, year: number): Promise<any | null> {
    const records = await this.getZakatRecords(userId);
    return records.find((z: any) => z.year === year) || null;
  }

  async getHajjPlanByYear(userId: string, targetYear: number): Promise<any | null> {
    const plans = await this.getHajjPlans(userId);
    return plans.find((p: any) => p.targetYear === targetYear) || null;
  }

  async updateUserTier(id: string, tier: string): Promise<void> {
    await this.updateUser(id, { tier });
  }

  // ============================================================
  // DASHBOARD
  // ============================================================
  async getDashboardData(userId: string): Promise<any> {
    await this.init();
    const userRes = await this.db!.query('SELECT * FROM users WHERE id = ?', [userId]);
    const user = userRes.values?.[0];
    const takaful = await this.getTakafulPlan(userId);
    const retirement = await this.getRetirementPlan(userId);
    const zakat = await this.getZakatRecords(userId);

    return {
      user: user ? this.mapUser(user) : null,
      financials: { netWorth: 0, totalAssets: 0, totalLiabilities: 0, monthlyIncome: 0, monthlyExpenses: 0, monthlySavings: 0 },
      healthScore: { score: user?.health_score || 0, zone: user?.health_score_zone || 'CRITICAL' },
      takaful, retirement, zakat,
    };
  }

  private mapUser(row: any): any {
    return {
      id: row.id, email: row.email, password: row.password,
      fullName: row.full_name, familyStatus: row.family_status,
      tier: row.tier, healthScore: row.health_score,
      healthScoreZone: row.health_score_zone,
      createdAt: row.created_at, updatedAt: row.updated_at,
    };
  }
}

// ============================================================
// SINGLETON: Auto-detect platform and export correct service
// ============================================================

let dbService: any = null;

function createDbService() {
  const platform = getPlatform();
  console.log('Platform detected:', platform);

  if (isNative()) {
    console.log('Using REAL SQLite (native)');
    return new CapacitorDbService();
  } else {
    console.log('Using MOCK DB (web/browser)');
    return new MockDbService();
  }
}

export const capacitorDbService = createDbService();
export default capacitorDbService;
